import {useEffect, useState} from 'react';
import {calcToastsBottomEdge, TOASTS_AVOID_ATTRIBUTE} from './toastsBottomEdge';

const AVOID_SELECTOR = `[${TOASTS_AVOID_ATTRIBUTE}]`;

/**
 * Отступ стека уведомлений от низа окна — над нижним HUD под стеком
 * (toastsBottomEdge). Пересчет — как у свободной области поля
 * (useFieldInsets): на изменение размеров помеченных элементов, на перестройку
 * дерева (футер сворачивается, тулбары появляются), на окно и на конец
 * CSS-переходов; не чаще раза за кадр
 *
 * @param {{current: HTMLElement|null}} stackRef — сам стек: его горизонталь
 * @param {*} [layoutKey] — раскладка: при ее смене угловые элементы
 *   монтируются заново
 * @returns {number|null} CSS-пиксели; null — еще не мерили
 */
export const useToastsBottomEdge = (stackRef, layoutKey) => {
  const [edge, setEdge] = useState(null);

  useEffect(() => {
    if (typeof document === 'undefined') {
      return undefined;
    }

    let frameId = null;
    const observed = new Set();
    const resizeObserver = typeof ResizeObserver === 'function'
      ? new ResizeObserver(() => scheduleMeasure())
      : null;

    const measure = () => {
      frameId = null;
      const elements = document.querySelectorAll(AVOID_SELECTOR);
      if (resizeObserver) {
        elements.forEach((element) => {
          if (!observed.has(element)) {
            resizeObserver.observe(element);
            observed.add(element);
          }
        });
      }
      const rects = [...elements].map((element) => element.getBoundingClientRect());
      const stackRect = stackRef.current?.getBoundingClientRect();
      const span = stackRect ? {left: stackRect.left, right: stackRect.right} : null;
      setEdge(calcToastsBottomEdge(rects, window.innerHeight, span));
    };

    function scheduleMeasure() {
      if (frameId === null) {
        frameId = window.requestAnimationFrame(measure);
      }
    }

    const mutationObserver = typeof MutationObserver === 'function'
      ? new MutationObserver(scheduleMeasure)
      : null;
    mutationObserver?.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: [TOASTS_AVOID_ATTRIBUTE, 'class', 'style'],
    });
    window.addEventListener('resize', scheduleMeasure);
    // Футер сворачивается, панели выезжают CSS-переходом: конечное
    // положение — по его концу
    document.addEventListener('transitionend', scheduleMeasure, true);

    measure();

    return () => {
      if (frameId !== null) {
        window.cancelAnimationFrame(frameId);
      }
      resizeObserver?.disconnect();
      mutationObserver?.disconnect();
      window.removeEventListener('resize', scheduleMeasure);
      document.removeEventListener('transitionend', scheduleMeasure, true);
    };
  }, [stackRef, layoutKey]);

  return edge;
};
